import React, { useState, useEffect } from 'react';
import { getMarketingActivities, getMarketingStatistics, createMarketingActivity, updateMarketingActivity, deleteMarketingActivity, getStores } from '../api';
import Pagination from '../components/Pagination';

function Marketing() {
  const user = JSON.parse(sessionStorage.getItem('health_user') || '{}');
  const isAdmin = user?.role === 'admin';
  const [activities, setActivities] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('全部');
  const [storeFilter, setStoreFilter] = useState('');
  const [stores, setStores] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 20, total: 0, totalPages: 0 });
  const [sortInfo, setSortInfo] = useState({ field: 'created_at', order: 'desc' });
  const [formData, setFormData] = useState({ name: '', type: '', channel: '', start_date: '', end_date: '', participant_count: '', budget: '', status: '草稿', description: '', store_id: '' });

  const statuses = ['全部', '草稿', '进行中', '已完成'];
  const types = ['促销活动', '主题活动', '知识讲座', '节日活动'];
  const channels = ['线上推广', '线下地推', '老客推荐', '异业合作', '自然流量'];

  useEffect(() => { fetchStores(); }, []);
  useEffect(() => { fetchData(); }, [filterStatus, storeFilter, pagination.page, pagination.pageSize, sortInfo]);

  const fetchStores = async () => {
    if (!isAdmin) return;
    try {
      const res = await getStores();
      if (res.data.success) setStores(res.data.data);
    } catch (e) { console.error(e); }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const map = { '全部': '', '草稿': 'draft', '进行中': 'ongoing', '已完成': 'completed' };
      const [actsRes, statsRes] = await Promise.all([
        getMarketingActivities({
          status: map[filterStatus],
          store_id: storeFilter || undefined,
          page: pagination.page,
          pageSize: pagination.pageSize,
          sortBy: sortInfo.field,
          sortOrder: sortInfo.order
        }),
        getMarketingStatistics()
      ]);
      if (actsRes.data.success) {
        setActivities(actsRes.data.data);
        if (actsRes.data.pagination) setPagination(prev => ({ ...prev, ...actsRes.data.pagination }));
      }
      if (statsRes.data.success) setStats(statsRes.data.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handlePageChange = (page) => setPagination(prev => ({ ...prev, page }));
  const handlePageSizeChange = (pageSize) => setPagination(prev => ({ ...prev, pageSize, page: 1 }));
  const handleSortChange = (field, order) => setSortInfo({ field, order });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const statusMap = { '草稿': 'draft', '进行中': 'ongoing', '已完成': 'completed' };
      const data = { ...formData, status: statusMap[formData.status] || 'draft' };
      if (editItem) await updateMarketingActivity(editItem.id, data);
      else await createMarketingActivity(data);
      setShowModal(false);
      resetForm();
      fetchData();
    } catch (e) { console.error(e); }
  };

  const handleEdit = (item) => {
    setEditItem(item);
    const statusMap = { draft: '草稿', ongoing: '进行中', completed: '已完成' };
    const formatDate = (d) => d ? d.split('T')[0] : '';
    setFormData({ name: item.name, type: item.type || '', channel: item.channel || '', start_date: formatDate(item.start_date), end_date: formatDate(item.end_date), participant_count: item.participant_count || '', budget: item.budget || '', status: statusMap[item.status] || '草稿', description: item.description || '', store_id: item.store_id || '' });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('确定删除?')) {
      try {
        await deleteMarketingActivity(id);
        fetchData();
      } catch (e) {
        alert(e.response?.data?.message || '删除失败');
      }
    }
  };

  const resetForm = () => {
    setEditItem(null);
    setFormData({ name: '', type: '', channel: '', start_date: '', end_date: '', participant_count: '', budget: '', status: '草稿', description: '', store_id: '' });
  };

  const getStatusBadge = (status) => {
    const map = { draft: '草稿', ongoing: '进行中', completed: '已完成' };
    const cls = { draft: 'badge-gray', ongoing: 'badge-yellow', completed: 'badge-green' };
    return <span className={`status-badge ${cls[status]}`}>{map[status] || status}</span>;
  };

  return (
    <div>
      <div className="stats-cards">
        <div className="stats-row">
          <div className="stat-box"><div className="stat-label">活动总数</div><div className="stat-value">{stats.totalActivities || 0}</div></div>
          <div className="stat-box"><div className="stat-label">总预算</div><div className="stat-value">¥{(stats.totalBudget || 0).toLocaleString()}</div></div>
          <div className="stat-box"><div className="stat-label">参与人数</div><div className="stat-value">{stats.totalParticipants || 0}</div></div>
          <div className="stat-box"><div className="stat-label">进行中</div><div className="stat-value orange">{stats.ongoingCount || 0}</div></div>
        </div>
      </div>
      <div className="content-card">
        <div className="section-title">营销活动管理</div>
        <div className="toolbar-row">
          <div className="toolbar-left">
            <select className="filter-select" value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setPagination(p => ({ ...p, page: 1 })); }}>
              {statuses.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            {isAdmin && (
              <select className="filter-select" value={storeFilter} onChange={e => { setStoreFilter(e.target.value); setPagination(p => ({ ...p, page: 1 })); }}>
                <option value="">全部门店</option>
                {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            )}
          </div>
          <div className="toolbar-right">
            <button className="action-btn" onClick={() => {
              const csv = ['序号,活动名称,类型,渠道,时间范围,参与人数,预算费用,状态'];
              activities.forEach((a, i) => { csv.push(`${i+1},"${a.name}","${a.type||''}","${a.channel||''}","${a.start_date&&a.end_date?a.start_date+' ~ '+a.end_date:''}","${a.participant_count||0}","${a.budget||0}","${a.status}"`); });
              const blob = new Blob([csv.join('\n')], { type: 'text/csv;charset=utf-8;' });
              const url = URL.createObjectURL(blob);
              const link = document.createElement('a'); link.href = url; link.download = `营销活动_${new Date().toISOString().slice(0,10)}.csv`; link.click();
            }}>导出</button>
            <button className="action-btn btn-blue" onClick={() => { resetForm(); setShowModal(true); }}>+ 新增活动</button>
          </div>
        </div>
        {loading ? <div className="loading-text">加载中...</div> : (
          <table className="basic-table">
            <thead>
              <tr>
                <th className="no-cell">序号</th>
                <th>门店</th>
                <th onClick={() => handleSortChange('name', sortInfo.field === 'name' ? (sortInfo.order === 'asc' ? 'desc' : 'asc') : 'asc')} style={{cursor:'pointer'}}>活动名称{sortInfo.field==='name' && <span>{sortInfo.order==='asc'?'↑':'↓'}</span>}</th>
                <th>类型</th><th>渠道</th><th>时间范围</th>
                <th>参与人数</th>
                <th onClick={() => handleSortChange('budget', sortInfo.field === 'budget' ? (sortInfo.order === 'asc' ? 'desc' : 'asc') : 'asc')} style={{cursor:'pointer'}}>预算费用{sortInfo.field==='budget' && <span>{sortInfo.order==='asc'?'↑':'↓'}</span>}</th>
                <th>状态</th><th>操作</th>
              </tr>
            </thead>
            <tbody>
              {activities.length > 0 ? activities.map((a, i) => (
                <tr key={a.id}>
                  <td className="no-cell">{i + 1}</td><td>{a.store_name || '-'}</td><td>{a.name}</td><td>{a.type || '-'}</td><td>{a.channel || '-'}</td>
                  <td>{a.start_date && a.end_date ? `${a.start_date} ~ ${a.end_date}` : '-'}</td><td>{a.participant_count || 0}</td>
                  <td>¥{a.budget || 0}</td><td>{getStatusBadge(a.status)}</td>
                  <td><button className="btn-xs btn-blue" onClick={() => handleEdit(a)}>编辑</button><button className="btn-xs btn-red" onClick={() => handleDelete(a.id)}>删除</button></td>
                </tr>
              )) : <tr><td colSpan="10" style={{textAlign:'center',color:'#999'}}>暂无数据</td></tr>}
            </tbody>
          </table>
        )}
        <Pagination pagination={pagination} onPageChange={handlePageChange} onPageSizeChange={handlePageSizeChange} />
      </div>
      {showModal && (
        <div className="modal-mask">
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div className="modal-title"><span>{editItem ? '编辑活动' : '新增活动'}</span><span className="modal-close" onClick={() => setShowModal(false)}>×</span></div>
            <form onSubmit={handleSubmit}>
              <div className="modal-content">
                <div className="form-tip">带 <span style={{color:'#ff4d4f'}}>*</span> 号的为必填项</div>
                {isAdmin && (
                  <div className="form-row"><div className="form-field"><label className="form-label">门店</label>
                    <select className="form-input" value={formData.store_id} onChange={e => setFormData({...formData, store_id: e.target.value})}>
                      <option value="">请选择</option>
                      {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                  </div></div>
                )}
                <div className="form-row"><div className="form-field"><label className="form-label">活动名称 *</label><input className="form-input" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} required /></div></div>
                <div className="form-row"><div className="form-field"><label className="form-label">类型</label><select className="form-input" value={formData.type} onChange={e => setFormData({...formData, type: e.target.value})}><option value="">请选择</option>{types.map(t => <option key={t} value={t}>{t}</option>)}</select></div></div>
                <div className="form-row"><div className="form-field"><label className="form-label">渠道</label><select className="form-input" value={formData.channel || ''} onChange={e => setFormData({...formData, channel: e.target.value})}><option value="">请选择</option>{channels.map(c => <option key={c} value={c}>{c}</option>)}</select></div></div>
                <div className="form-row"><div className="form-field"><label className="form-label">开始日期</label><input className="form-input" type="date" value={formData.start_date} onChange={e => setFormData({...formData, start_date: e.target.value})} /></div><div className="form-field"><label className="form-label">结束日期</label><input className="form-input" type="date" value={formData.end_date} onChange={e => setFormData({...formData, end_date: e.target.value})} /></div></div>
                <div className="form-row"><div className="form-field"><label className="form-label">参与人数</label><input className="form-input" type="number" value={formData.participant_count} onChange={e => setFormData({...formData, participant_count: e.target.value})} /></div><div className="form-field"><label className="form-label">预算费用(元)</label><input className="form-input" type="number" value={formData.budget} onChange={e => setFormData({...formData, budget: e.target.value})} /></div></div>
                <div className="form-row"><div className="form-field"><label className="form-label">状态</label><select className="form-input" value={formData.status} onChange={e => setFormData({...formData, status: e.target.value})}>{statuses.slice(1).map(s => <option key={s} value={s}>{s}</option>)}</select></div></div>
                <div className="form-row"><div className="form-field"><label className="form-label">描述</label><textarea className="form-input" rows="3" value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} /></div></div>
              </div>
              <div className="modal-foot"><button type="button" style={{ marginRight: 12 }} onClick={() => setShowModal(false)}>取消</button><button type="submit" className="action-btn btn-blue">保存</button></div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Marketing;